/**
 * **投资域另外三个对话框**：记收益 / 改价 / 批量改价 —— 批 `99` B2
 *
 * 依据：`投资域PRD` v2 §4.2 ③（动作区）· §6.3（分红结息）· `AC-INV8`（不动数量与成本）·
 * `AC-INV14`（改价只改 `current_price`，其余五项与之联动）· `DR-059` D6（改价是 P0）。
 *
 * ## 🔴 为什么「改价」必须存在（不是"增强"）
 *
 * 实测 2026-10-24：`holdings.current_price` 是 v1 迁移快照，**全项目没有任何写入口**。
 * 不补这个，市值与三种回报率会永久停在迁移日 —— 等于交付一个不会动的仪表盘。
 *
 * ## 🔴 三个对话框都只做一件事
 *
 * | 对话框 | 写什么 | 绝对不能写什么 |
 * |---|---|---|
 * | 记收益 | 1 条 `in` 腿 + `investment_transactions`（`dividend`/`settle`） | `quantity` / `costPrice`（分红不是卖出） |
 * | 改价（单条） | `holdings.current_price` | 数量 / 成本价 / 已实现盈亏 |
 * | 批量改价 | 同上，逐条 | 同上；**留空表示这条不改**（不是改成 0） |
 */

import { useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatMoneyText, type Cents, type HoldingView } from '@jiaying/shared';
import {
  batchUpdateHoldingPrices,
  createInvestmentIncome,
  updateHoldingPrice,
} from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { invalidateAfterTxnWrite } from '../features/invalidate.js';
import {
  actionsOf,
  buildBatchPriceRequest,
  buildIncomeRequest,
  buildPriceRequest,
  canSubmitBatchPrice,
  emptyIncomeDraft,
  holdingTitleOf,
  kindLabelOf,
  priceTextOf,
  qtyTextOf,
  validateIncomeDraft,
  type IncomeDraft,
} from '../features/investment.js';
import {
  BTN_CANCEL,
  BTN_SAVE,
  DLG_BATCH_PRICE_TITLE,
  DLG_INCOME_TITLE,
  DLG_PRICE_TITLE,
  F_DATE,
  F_INCOME_AMOUNT,
  F_NEW_PRICE,
  F_NOTE,
  HINT_BATCH_PRICE,
  HINT_INCOME,
  HINT_PRICE,
} from '../features/investment-labels.js';

/** 三个对话框共用的提交失败展示 */
function submitErrorText(e: unknown): string {
  return e instanceof ApiError ? e.message : String(e);
}

// ─────────────────────────── ① 记收益 ───────────────────────────

export interface InvestmentIncomeDialogProps {
  holding: HoldingView;
  today: string;
  onClose: () => void;
}

export function InvestmentIncomeDialog({
  holding,
  today,
  onClose,
}: InvestmentIncomeDialogProps): ReactElement {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<IncomeDraft>(() => emptyIncomeDraft(holding, today));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const patch = (p: Partial<IncomeDraft>): void => {
    setDraft((d) => ({ ...d, ...p }));
    setSubmitError(null);
  };

  const save = useMutation({
    mutationFn: () => createInvestmentIncome(buildIncomeRequest(draft)),
    onSuccess: () => {
      // 分红写的是资金腿 ⇒ 账户余额与净资产要失效；持仓数量不变但仍刷一次概览（累计分红变了）
      invalidateAfterTxnWrite(qc);
      void qc.invalidateQueries({ queryKey: ['investment-overview'] });
      onClose();
    },
    onError: (e) => {
      setSubmitError(submitErrorText(e));
    },
  });

  const submit = (): void => {
    const e = validateIncomeDraft(draft);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    save.mutate();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {DLG_INCOME_TITLE} · {holdingTitleOf(holding)}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="caption" color="text.secondary">
            {kindLabelOf(holding.kind)} · {actionsOf(holding.kind).income}
          </Typography>

          <TextField
            size="small"
            required
            fullWidth
            inputMode="decimal"
            label={`${F_INCOME_AMOUNT}（元）`}
            value={draft.amountText}
            onChange={(e) => patch({ amountText: e.target.value })}
            error={errors.amountText !== undefined}
            helperText={errors.amountText}
            inputProps={{ 'data-testid': 'income-amount' }}
          />

          <TextField
            size="small"
            required
            fullWidth
            type="date"
            label={F_DATE}
            value={draft.date}
            onChange={(e) => patch({ date: e.target.value })}
            error={errors.date !== undefined}
            helperText={errors.date}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'income-date' }}
          />

          <TextField
            size="small"
            fullWidth
            label={F_NOTE}
            value={draft.note}
            onChange={(e) => patch({ note: e.target.value })}
            inputProps={{ 'data-testid': 'income-note' }}
          />

          <Divider />
          <Typography variant="caption" color="text.secondary">
            {HINT_INCOME}
          </Typography>
          {submitError !== null && <Alert severity="error">{submitError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          {BTN_CANCEL}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={save.isPending}
          data-testid="income-submit"
        >
          {save.isPending ? '保存中…' : BTN_SAVE}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────── ② 改价（单条） ───────────────────────────

export interface HoldingPriceDialogProps {
  holding: HoldingView;
  onClose: () => void;
}

export function HoldingPriceDialog({ holding, onClose }: HoldingPriceDialogProps): ReactElement {
  const qc = useQueryClient();
  // 预填当前现价（用户通常只改一两位）
  const [text, setText] = useState(() => priceTextOf(holding.currentPriceE4));
  const [submitError, setSubmitError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (priceE4: number) => updateHoldingPrice(holding.id, { currentPriceE4: priceE4 }),
    onSuccess: () => {
      // 市值 / 浮盈 / 三个回报率会同时变（`AC-INV14`）⇒ 概览 + 净资产都要刷
      void qc.invalidateQueries({ queryKey: ['investment-overview'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
      onClose();
    },
    onError: (e) => {
      setSubmitError(submitErrorText(e));
    },
  });

  const req = buildPriceRequest(text);

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {DLG_PRICE_TITLE} · {holdingTitleOf(holding)}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              当前市值{' '}
              {formatMoneyText(holding.marketValueCents as Cents, holding.currency)} · 份额{' '}
              {qtyTextOf(holding)}
            </Typography>
          </Box>

          <TextField
            size="small"
            required
            fullWidth
            inputMode="decimal"
            label={`${F_NEW_PRICE}（元/份）`}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setSubmitError(null);
            }}
            error={text.trim() !== '' && req === null}
            helperText={text.trim() !== '' && req === null ? '价格须大于 0，最多四位小数' : HINT_PRICE}
            inputProps={{ 'data-testid': 'price-input' }}
          />

          {submitError !== null && <Alert severity="error">{submitError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          {BTN_CANCEL}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (req !== null) save.mutate(req.currentPriceE4);
          }}
          disabled={req === null || save.isPending}
          data-testid="price-submit"
        >
          {save.isPending ? '保存中…' : BTN_SAVE}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────── ③ 批量改价 ───────────────────────────

export interface BatchPriceDialogProps {
  /** 全部持仓（页面已排好序；已清仓的由页面过滤掉 —— 市值恒 0，改价无意义） */
  holdings: readonly HoldingView[];
  onClose: () => void;
}

export function BatchPriceDialog({ holdings, onClose }: BatchPriceDialogProps): ReactElement {
  const qc = useQueryClient();
  /** `holdingId → 新价文本`；**键不存在 = 这条不改** */
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const entries = holdings.map((h) => ({ holdingId: h.id, text: texts[h.id] ?? '' }));
  const req = buildBatchPriceRequest(entries);

  const save = useMutation({
    mutationFn: () => batchUpdateHoldingPrices(req!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['investment-overview'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
      onClose();
    },
    onError: (e) => {
      setSubmitError(submitErrorText(e));
    },
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{DLG_BATCH_PRICE_TITLE}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            {HINT_BATCH_PRICE}
          </Typography>

          {holdings.map((h) => (
            <Stack
              key={h.id}
              direction="row"
              spacing={2}
              alignItems="center"
              data-testid={`batch-price-row-${h.id}`}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {holdingTitleOf(h)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {kindLabelOf(h.kind)} · 现价 {priceTextOf(h.currentPriceE4)}
                </Typography>
              </Box>
              <TextField
                size="small"
                inputMode="decimal"
                placeholder="留空不改"
                value={texts[h.id] ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setTexts((t) => ({ ...t, [h.id]: v }));
                  setSubmitError(null);
                }}
                sx={{ width: 150, flexShrink: 0 }}
                inputProps={{ 'data-testid': `batch-price-input-${h.id}` }}
              />
            </Stack>
          ))}

          {submitError !== null && <Alert severity="error">{submitError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          {BTN_CANCEL}
        </Button>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          disabled={!canSubmitBatchPrice(entries) || save.isPending}
          data-testid="batch-price-submit"
        >
          {save.isPending ? '保存中…' : BTN_SAVE}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
